import { Slide } from "@shared/types";
import { moveItem } from "./slide-factory";

export const UNDO_LIMIT = 20;

/** How to reverse a structural edit. Each is applied to the CURRENT slides
 *  (not a snapshot), so field edits made after the operation survive undo. */
export type UndoEntry =
  | { kind: "reinsert"; label: string; removed: { slide: Slide; index: number }[] }
  | { kind: "remove"; label: string; ids: string[] }
  | { kind: "enabled"; label: string; previous: Record<string, boolean> }
  | { kind: "order"; label: string; order: string[] };

/** A structural edit's result, or null when it changes nothing. */
export type OpResult = { slides: Slide[]; undo: UndoEntry } | null;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function removeSlides(slides: Slide[], ids: Iterable<string>): OpResult {
  const drop = new Set(ids);
  const removed: { slide: Slide; index: number }[] = [];
  const kept: Slide[] = [];
  slides.forEach((slide, index) => {
    if (drop.has(slide.id)) removed.push({ slide, index });
    else kept.push(slide);
  });
  if (!removed.length) return null;
  const label = removed.length === 1 ? "remove slide" : `remove ${plural(removed.length, "slide")}`;
  return { slides: kept, undo: { kind: "reinsert", label, removed } };
}

/** Put removed slides back at their original positions (clamped to the
 *  current length). Slides whose id is already present are skipped. */
export function reinsertSlides(slides: Slide[], removed: { slide: Slide; index: number }[]): Slide[] {
  const out = [...slides];
  const present = new Set(out.map((s) => s.id));
  for (const { slide, index } of [...removed].sort((a, b) => a.index - b.index)) {
    if (present.has(slide.id)) continue;
    out.splice(Math.min(Math.max(index, 0), out.length), 0, slide);
    present.add(slide.id);
  }
  return out;
}

export function removeIds(slides: Slide[], ids: Iterable<string>): Slide[] {
  const drop = new Set(ids);
  return slides.filter((s) => !drop.has(s.id));
}

/** A deep copy of a slide under a new id. */
export function copySlide(slide: Slide, id: string): Slide {
  return { ...(JSON.parse(JSON.stringify(slide)) as Slide), id };
}

/** Insert a copy of each given slide directly after its original. */
export function duplicateSlides(
  slides: Slide[],
  ids: Iterable<string>,
  newId: () => string = () => crypto.randomUUID(),
): (NonNullable<OpResult> & { newIds: string[] }) | null {
  const want = new Set(ids);
  const out: Slide[] = [];
  const newIds: string[] = [];
  for (const slide of slides) {
    out.push(slide);
    if (want.has(slide.id)) {
      const copy = copySlide(slide, newId());
      out.push(copy);
      newIds.push(copy.id);
    }
  }
  if (!newIds.length) return null;
  const label = newIds.length === 1 ? "duplicate" : `duplicate ${plural(newIds.length, "slide")}`;
  return { slides: out, newIds, undo: { kind: "remove", label, ids: newIds } };
}

/** Append new slides (e.g. generated holiday closures) as one undoable step. */
export function appendSlides(slides: Slide[], added: Slide[], label: string): OpResult {
  if (!added.length) return null;
  return { slides: [...slides, ...added], undo: { kind: "remove", label, ids: added.map((s) => s.id) } };
}

export function setSlidesEnabled(
  slides: Slide[],
  ids: Iterable<string>,
  enabled: boolean,
  label = `turn ${enabled ? "on" : "off"}`,
): OpResult {
  const target = new Set(ids);
  const previous: Record<string, boolean> = {};
  const next = slides.map((s) => {
    if (!target.has(s.id) || s.enabled === enabled) return s;
    previous[s.id] = s.enabled;
    return { ...s, enabled };
  });
  if (!Object.keys(previous).length) return null;
  return { slides: next, undo: { kind: "enabled", label, previous } };
}

export function restoreEnabled(slides: Slide[], previous: Record<string, boolean>): Slide[] {
  return slides.map((s) =>
    s.id in previous && s.enabled !== previous[s.id] ? { ...s, enabled: previous[s.id] } : s,
  );
}

export function moveSlide(slides: Slide[], from: number, to: number): OpResult {
  const next = moveItem(slides, from, to);
  if (next === slides) return null;
  return { slides: next, undo: { kind: "order", label: "move", order: slides.map((s) => s.id) } };
}

/** Reorder to match a previous id order; slides not in it keep their
 *  relative order at the end. */
export function restoreOrder(slides: Slide[], order: string[]): Slide[] {
  const pos = new Map(order.map((id, i) => [id, i]));
  const known = slides.filter((s) => pos.has(s.id)).sort((a, b) => pos.get(a.id)! - pos.get(b.id)!);
  const unknown = slides.filter((s) => !pos.has(s.id));
  return [...known, ...unknown];
}

export function applyUndo(slides: Slide[], entry: UndoEntry): Slide[] {
  switch (entry.kind) {
    case "reinsert":
      return reinsertSlides(slides, entry.removed);
    case "remove":
      return removeIds(slides, entry.ids);
    case "enabled":
      return restoreEnabled(slides, entry.previous);
    case "order":
      return restoreOrder(slides, entry.order);
  }
}

export function pushUndo(stack: UndoEntry[], entry: UndoEntry, max = UNDO_LIMIT): UndoEntry[] {
  return [...stack, entry].slice(-max);
}

/** The selection minus ids that no longer exist. Returns the same Set when
 *  nothing was dropped, so callers can skip a state update. */
export function pruneSelection(selected: Set<string>, slides: Slide[]): Set<string> {
  const ids = new Set(slides.map((s) => s.id));
  const kept = [...selected].filter((id) => ids.has(id));
  return kept.length === selected.size ? selected : new Set(kept);
}

export interface GroupSummary {
  name: string;
  count: number;
  /** Every slide in the group is enabled. */
  enabled: boolean;
}

/** Slide-level group names in first-appearance order. Names are trimmed the
 *  same way the server normalizes them; group references are ignored. */
export function groupSummaries(slides: Slide[]): GroupSummary[] {
  const byName = new Map<string, GroupSummary>();
  for (const s of slides) {
    if (s.type === "groupref") continue;
    const name = s.group?.trim();
    if (!name) continue;
    const g = byName.get(name) ?? { name, count: 0, enabled: true };
    g.count += 1;
    g.enabled &&= s.enabled;
    byName.set(name, g);
  }
  return [...byName.values()];
}

export function groupSlideIds(slides: Slide[], name: string): string[] {
  return slides.filter((s) => s.type !== "groupref" && s.group?.trim() === name).map((s) => s.id);
}

/** A short human label for a collapsed row; "" when there's nothing useful. */
export function slideLabel(slide: Slide): string {
  switch (slide.type) {
    case "text":
      return slide.title;
    case "image":
    case "video":
      return slide.fileName ?? "";
    case "team":
      return slide.name;
    case "youtube":
    case "vimeo":
      return slide.title || slide.videoId;
    case "qr":
      return slide.title || slide.caption || slide.data;
    case "review":
      return slide.author || slide.quote;
    case "clock":
      return slide.showWeather && slide.locationName ? `Clock · ${slide.locationName}` : "Clock";
    case "groupref":
      return slide.name ?? "";
  }
}

/**
 * Slides that were ADDED on the server after `base` (the version an editor
 * started from) and aren't in the editor's local list. With no base snapshot,
 * every server slide missing locally counts — which can bring back a slide the
 * admin deleted, so callers should ask them to review.
 */
export function slidesAddedSince(local: Slide[], latest: Slide[], base: Slide[] | null): Slide[] {
  const localIds = new Set(local.map((s) => s.id));
  const baseIds = base ? new Set(base.map((s) => s.id)) : null;
  return latest.filter((s) => !localIds.has(s.id) && (!baseIds || !baseIds.has(s.id)));
}
